import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';

// Mobile-specific login page (mirrors desktop logic, adjusts navigation + internal links to /m namespace)
export default function MobileLoginPage(){
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(localStorage.getItem('remember_me_flag') === 'true');
  const [healthCheckStatus, setHealthCheckStatus] = useState({ checking:true, healthy:false, responseTime:null, error:null });

  const { login } = useAuth();
  const { getCurrentUser, getEcosystemHealth } = useData();
  const navigate = useNavigate();

  // If already authenticated, redirect to mobile courses dashboard equivalent.
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const userData = await getCurrentUser();
        if (userData) navigate('/m/courses');
      } catch { /* silent */ }
    };
    checkExistingAuth();
  }, [getCurrentUser, navigate]);

  useEffect(() => {
    const runHealthCheck = async () => {
      setHealthCheckStatus({ checking:true, healthy:false, responseTime:null, error:null });
      const start = Date.now();
      try {
        const info = await getEcosystemHealth(true);
        const ms = Date.now() - start;
        if(info){
          setHealthCheckStatus({ checking:false, healthy:true, responseTime:ms, error:null });
        } else {
          setHealthCheckStatus({ checking:false, healthy:false, responseTime:ms, error:'Service unavailable' });
        }
      } catch(err){
        const ms = Date.now() - start;
        setHealthCheckStatus({ checking:false, healthy:false, responseTime:ms, error:err.message });
      }
    };
    runHealthCheck();
  }, [getEcosystemHealth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    
    // Track login attempt
    try {
      UsageEventsTracker.recordUserAction('LOGIN_ATTEMPT', {
        action: 'click',
        category: 'auth',
        entityType: 'button',
        label: 'Mobile Login Button',
        metadata: { email, rememberMe, platform: 'mobile' }
      });
    } catch (trackError) {
      console.warn('MobileLoginPage: Failed to track login attempt', trackError);
    }
    
    try {
      const result = await login(email, password, rememberMe);
      if(result.success){
        navigate('/m/courses');
      } else {
        setErrorMessage(result.message || 'Login failed. Please try again.');
      }
    } catch(err){
      setErrorMessage(err.message || 'An error occurred during login.');
    } finally {
      setLoading(false);
    }
  };

  function renderHealth(){
    if(healthCheckStatus.checking){
      return <div className="health-status checking"><div className="health-spinner"></div><span>Checking system status...</span></div>;
    }
    if(healthCheckStatus.healthy){
      return <div className="health-status healthy"><div className="health-message">✅ All systems operational {healthCheckStatus.responseTime && <span className="response-time">({healthCheckStatus.responseTime}ms)</span>}</div></div>;
    }
    return <div className="health-status error"><div className="health-message">⚠️ Service issues detected</div>{healthCheckStatus.error && <div className="health-details"><small>{healthCheckStatus.error}</small></div>}</div>;
  }

  return (
    <div className="login-page m-screen">
      <div className="login-form m-mobile-form">
        <h2>Login to DocuHounds</h2>
        {renderHealth()}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <form onSubmit={handleSubmit}>
          <input type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} required />
          <input type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required />
          <div className="remember-me-row">
            <label htmlFor="rememberMe" className="remember-label">
              <input id="rememberMe" type="checkbox" checked={rememberMe} onChange={e=>setRememberMe(e.target.checked)} />
              <span>Remember me</span>
            </label>
          </div>
          <button type="submit" className="login-button" disabled={loading || !healthCheckStatus.healthy || !email || !password}>
            {loading ? <><div className="button-spinner"></div>Signing in...</> : 'Sign In'}
          </button>
        </form>
        <div className="login-footer">
          <p>Don't have an account? <Link to="/m/register">Sign up here</Link></p>
          <p><Link to="/m/forgot-password">Forgot your password?</Link></p>
        </div>
      </div>
    </div>
  );
}
